import Base from 'ember-simple-auth/authenticators/base';
import { apiFetch } from '../fetch';

interface BetterAuthSession {
  accessToken: string;
  user?: {
    id: string;
    email: string;
    name?: string;
    role?: string;
  };
}

interface SignInResponse {
  token: string;
  user?: BetterAuthSession['user'];
}

interface SessionResponse {
  user?: BetterAuthSession['user'];
}

interface ErrorResponse {
  message?: string;
}

/** Arguments for token-based (magic link) authentication. */
interface TokenAuthArgs {
  token: string;
}

/**
 * Custom ember-simple-auth authenticator for the Favoris API
 * which uses better-auth with Bearer token authentication.
 */
export default class BetterAuthAuthenticator extends Base {
  private authorizationHeader(accessToken: string): { Authorization: string } {
    return { Authorization: `Bearer ${accessToken}` };
  }

  async authenticate(args: TokenAuthArgs): Promise<BetterAuthSession>;
  async authenticate(
    email: string,
    password: string,
    rememberMe?: boolean
  ): Promise<BetterAuthSession>;
  async authenticate(
    emailOrArgs: string | TokenAuthArgs,
    password?: string,
    rememberMe = true
  ): Promise<BetterAuthSession> {
    // Token-based (magic link) path
    if (typeof emailOrArgs === 'object') {
      const { token } = emailOrArgs;
      const sessionResponse = await apiFetch('/api/auth/get-session', {
        headers: this.authorizationHeader(token),
      });
      if (!sessionResponse.ok) {
        throw new Error('Invalid or expired magic link token');
      }
      const session = (await sessionResponse.json()) as SessionResponse;
      return { accessToken: token, user: session.user };
    }

    // Email + password path
    const email = emailOrArgs;
    const response = await apiFetch('/api/auth/sign-in/email', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, rememberMe }),
    });

    if (!response.ok) {
      const error = (await response
        .json()
        .catch(() => null)) as ErrorResponse | null;
      throw new Error(error?.message ?? `Login failed (${response.status})`);
    }

    const data = (await response.json()) as SignInResponse;
    const accessToken = data.token;

    // Fetch full session to get complete user data (including role from admin plugin)
    const sessionResponse = await apiFetch('/api/auth/get-session', {
      headers: this.authorizationHeader(accessToken),
    });
    const sessionUser = sessionResponse.ok
      ? ((await sessionResponse.json()) as SessionResponse).user
      : undefined;

    return {
      accessToken,
      user: sessionUser ?? data.user,
    };
  }

  async restore(data: BetterAuthSession): Promise<BetterAuthSession> {
    if (!data.accessToken) {
      throw new Error('No access token to restore');
    }

    // Validate the token by fetching the current session.
    //
    // On a network error (TypeError) we restore optimistically from the cached
    // token rather than throwing.  This prevents ember-simple-auth from wiping
    // localStorage when the request is temporarily unreachable — e.g. when
    // running inside SendIntentActivity (Android share sheet) whose WebView
    // may have transient mixed-content restrictions before the config takes
    // effect, or when the device is offline.  A real 401/403 from the server
    // still clears the session as expected.
    let response: Response;
    try {
      response = await apiFetch('/api/auth/get-session', {
        headers: this.authorizationHeader(data.accessToken),
      });
    } catch {
      return data;
    }

    if (!response.ok) {
      throw new Error('Session expired');
    }

    const session = (await response.json()) as SessionResponse;
    return {
      accessToken: data.accessToken,
      user: session.user ?? data.user,
    };
  }

  async invalidate(data: BetterAuthSession): Promise<void> {
    await apiFetch('/api/auth/sign-out', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...this.authorizationHeader(data.accessToken),
      },
      body: JSON.stringify({}),
    }).catch(() => {
      // Ignore errors during sign-out
    });
  }
}
